import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, subscriptionAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);

    // Check subscription status
    const checkSubscription = async (userId) => {
        if (!userId) return false;
        
        try {
            setIsCheckingSubscription(true);
            console.log('Checking subscription for user:', userId);
            
            // Check if the user is an admin or has an admin-set subscription in user.subscription.status
            const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
            if (currentUserData.role === 'admin') {
                console.log('User is admin - granting access');
                return true;
            }
            
            // Check for subscription set via Admin Panel (stored in user.subscription.status)
            if (currentUserData.subscription && ['active', 'trialing'].includes(currentUserData.subscription.status)) {
                console.log('User has admin-set active subscription in user.subscription.status');
                return true;
            }
            
            // Check with the API for an actual subscription
            try {
                const response = await subscriptionAPI.getCurrentSubscription();
                console.log('Subscription API response:', response);
                
                // Backend returns the subscription directly (not wrapped in { subscription: ... })
                const sub = response.subscription || response;
                if (sub && ['active', 'trial'].includes(sub.status)) {
                    console.log('User has active or trial subscription via API');
                    return true;
                }
                
                console.log('No active subscription found via API');
                return false;
            } catch (err) {
                // If API call fails with a 404, it means no subscription exists
                if (err.response && err.response.status === 404) {
                    console.log('No subscription found (404 response)');
                    return false;
                }
                
                // For other errors, log but don't grant access
                console.error('Error checking subscription via API:', err);
                return false;
            }
        } catch (err) {
            console.error('Error in subscription check process:', err);
            return false;
        } finally {
            setIsCheckingSubscription(false);
        }
    };

    // Refresh user data from server
    const refreshUserData = async () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const data = await authAPI.getProfile();
                
                // Only update state if data has actually changed
                // This helps prevent unnecessary re-renders
                const newUserData = data.user;
                const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
                
                // Check subscription status
                const hasActiveSubscription = await checkSubscription(newUserData._id);
                newUserData.hasActiveSubscription = hasActiveSubscription;
                
                // If user data is the same, don't update state
                if (JSON.stringify({...newUserData, hasActiveSubscription: undefined}) === 
                    JSON.stringify({...currentUserData, hasActiveSubscription: undefined})) {
                    
                    // If only subscription status changed, update just that
                    if (newUserData.hasActiveSubscription !== currentUserData.hasActiveSubscription) {
                        const updatedUser = {...currentUserData, hasActiveSubscription};
                        localStorage.setItem('user', JSON.stringify(updatedUser));
                        setUser(updatedUser);
                    }
                    
                    return currentUserData;
                }
                
                // Update local storage and state only if data has changed
                localStorage.setItem('user', JSON.stringify(newUserData));
                setUser(newUserData);
                return newUserData;
            }
            return null;
        } catch (err) {
            console.error('Error refreshing user data:', err);
            return null;
        }
    };

    // Force refresh user data from server including subscription status
    const forceRefreshUserData = async () => {
        try {
            setLoading(true);
            console.log('Force refreshing user data from server...');
            const token = localStorage.getItem('token');
            if (token) {
                const data = await authAPI.getProfile();
                console.log('Received fresh user data:', data.user);
                
                // Store the complete user data including subscription
                localStorage.setItem('user', JSON.stringify(data.user));
                setUser(data.user);
                
                // Log subscription info for debugging
                if (data.user.subscription) {
                    console.log('User subscription from server:', data.user.subscription);
                } else {
                    console.log('No subscription data received from server');
                }
                
                return data.user;
            }
            return null;
        } catch (err) {
            console.error('Error force refreshing user data:', err);
            return null;
        } finally {
            setLoading(false);
        }
    };

    // Check if user is already logged in
    useEffect(() => {
        const initializeAuth = async () => {
            const token = localStorage.getItem('token');
            const savedUser = localStorage.getItem('user');
            
            if (token) {
                if (savedUser) {
                    // Set user from localStorage immediately
                    setUser(JSON.parse(savedUser));
                }
                
                // Then refresh user data from server to get latest info
                try {
                    await refreshUserData();
                } catch (err) {
                    console.warn('Could not refresh user data on startup:', err);
                    // Continue with cached user data
                }
            }
            setLoading(false);
        };
        
        initializeAuth();
    }, []);

    const register = async (username, email, password, options = {}) => {
        try {
            setError(null);
            const data = await authAPI.register(username, email, password, options);
            
            // Check subscription status immediately after registration
            const hasActiveSubscription = await checkSubscription(data.user._id);
            data.user.hasActiveSubscription = hasActiveSubscription;
            
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            setUser(data.user);
            return data;
        } catch (err) {
            console.error('Registration error:', err.response?.data || err.message);
            throw err.response?.data || { error: 'Registration failed. Please try again.' };
        }
    };

    const login = async (email, password) => {
        try {
            setError(null);
            const data = await authAPI.login(email, password);
            
            // Check subscription status immediately after login
            const hasActiveSubscription = await checkSubscription(data.user._id);
            data.user.hasActiveSubscription = hasActiveSubscription;
            
            // Set user data in localStorage
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            
            // Update auth state
            data.user.isAuthenticated = true;
            setUser(data.user);
            
            // Return data only after state has been set
            return data;
        } catch (err) {
            console.error('Login error:', err.response?.data || err.message);
            throw err.response?.data || { error: 'Login failed. Please try again.' };
        }
    };

    const logout = async () => {
        try {
            await authAPI.logout();
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

    const updateProfile = async (profileData) => {
        try {
            const data = await authAPI.updateProfile(profileData);
            const updatedUser = { ...user, ...data.user };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(updatedUser);
            return data;
        } catch (err) {
            console.error('Update profile error:', err);
            throw err;
        }
    };

    const uploadAvatar = async (file) => {
        try {
            const data = await authAPI.uploadAvatar(file);
            const updatedUser = { ...user, ...data.user };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(updatedUser);
            return data;
        } catch (err) {
            console.error('Avatar upload error:', err);
            throw err;
        }
    };

    const value = {
        user,
        loading: loading || isCheckingSubscription,
        error,
        isAuthenticated: !!user,
        register,
        login,
        logout,
        updateProfile,
        uploadAvatar,
        refreshUserData,
        checkSubscription,
        forceRefreshUserData
    };

    if (loading) {
        return null; // or a loading spinner
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
